import { isString } from 'lodash';
import { makeApi, makeBenefitsApi } from 'src/http/api/internal/internal-http';
import { CouponsRepository } from 'src/repositories/benefits/coupons.repository';
import { LpEventsRepository } from 'src/repositories/benefits/lp-events.repository';
import { LpEvent } from 'src/types/lp-events';
import { toBase64 } from 'src/utils/utils';
import { create } from 'zustand';

type States = {
  isLoadingLpEventsList: boolean;
  isLoadingLpEventDetails: boolean;
  isLoadingCreateLpEvent: boolean;
  isLoadingUpdateLpEvent: boolean;
  isLoadingBusinessUnits: boolean;
  
  isLoadingLpEventsByBusinessUnit: boolean;
  lpEventsList: any[];
  lpEventDetails: LpEvent | null;
  businessUnits: any[];
  lpEventsByBusinessUnit: any[];
};

type Actions = {
  getLpEventsList: () => Promise<void>;
  getLpEventDetails: (id: string) => Promise<any>;
  getBusinessUnits: () => Promise<void>;
  getAllBusinessUnits: () => Promise<void>;
  resetLpEventDetails: () => void;
  createLpEvent: (lpEvent: LpEvent) => Promise<void>;
  updateLpEvent: (id: string, lpEvent: LpEvent) => Promise<void>;
  getLpEventsByBusinessUnit: (businessUnitId: string) => Promise<any>;
  
};

type LpEvents = Actions & States;

const initialValues = {
  isLoadingLpEventsList: false,
  isLoadingLpEventDetails: false,
  isLoadingCreateLpEvent: false,
  isLoadingUpdateLpEvent: false,
  isLoadingBusinessUnits: false,
  
  isLoadingLpEventsByBusinessUnit: false,
  lpEventsList: [],
  lpEventDetails: null,
  businessUnits: [],
  lpEventsByBusinessUnit: [],
};

const lpEventsRepository = new LpEventsRepository(makeBenefitsApi());
const authRepository = new CouponsRepository(makeApi());

export const useLpEventsStore = create<LpEvents>((set, get) => ({
  ...initialValues,

  getLpEventsList: async () => {
    set({ isLoadingLpEventsList: true });
    const response = await lpEventsRepository.getLpEventsList();

    if (response.isLeft()) {
      set({ isLoadingLpEventsList: false });
      throw new Error(response.value.message);
    } else {
      set({ lpEventsList: response.value || [], isLoadingLpEventsList: false });
    }
  },

  getLpEventDetails: async (id: string) => {
    set({ isLoadingLpEventDetails: true });
    const response = await lpEventsRepository.getLpEventDetails(id);

    if (response.isLeft()) {
      set({ isLoadingLpEventDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ lpEventDetails: response.value, isLoadingLpEventDetails: false });
    }
  },

  getBusinessUnits: async () => {
    set({ isLoadingBusinessUnits: true });
    const response = await lpEventsRepository.getBusinessUnits();

    if (response.isLeft()) {
      set({ isLoadingBusinessUnits: false });
      throw new Error(response.value.message);
    } else {
      const businessUnits = (response.value || []).map((item: any) => ({
        value: item._id,
        label: `${item.name} - ${item.URLName}`,
        URLName: `${item.URLName}/`,
      }));
      set({ businessUnits, isLoadingBusinessUnits: false });
    }
  },

  getAllBusinessUnits: async () => {
    set({ isLoadingBusinessUnits: true });
    const response = await lpEventsRepository.getAllBusinessUnits();

    if (response.isLeft()) {
      set({ isLoadingBusinessUnits: false });
      throw new Error(response.value.message);
    } else {
      const businessUnits = (response.value || []).map((item: any) => ({
        value: item._id,
        label: `${item.name} - ${item.URLName}`,
        URLName: `${item.URLName}/`,
      }));
      set({ businessUnits, isLoadingBusinessUnits: false });
    }
  },

  createLpEvent: async (lpEvent) => {
    set({ isLoadingCreateLpEvent: true });

    if (!isString(lpEvent.customContent.loginPage.bannerImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.loginPage.bannerImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.loginPage.bannerImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.loginPage.bannerImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.loginPage.contentImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.loginPage.contentImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.loginPage.contentImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.loginPage.contentImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.registerPage.bannerImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.registerPage.bannerImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.registerPage.bannerImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.registerPage.bannerImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.registerPage.contentImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.registerPage.contentImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.registerPage.contentImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.registerPage.contentImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.homePage.bannerImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.homePage.bannerImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.homePage.bannerImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.homePage.bannerImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    const response = await lpEventsRepository.createLpEvent(lpEvent);

    if (response.isLeft()) {
      set({ isLoadingCreateLpEvent: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingCreateLpEvent: false });
    }
  },

  updateLpEvent: async (id, lpEvent) => {
    set({ isLoadingUpdateLpEvent: true });

    if (!isString(lpEvent.customContent.loginPage.bannerImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.loginPage.bannerImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.loginPage.bannerImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.loginPage.bannerImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.loginPage.contentImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.loginPage.contentImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.loginPage.contentImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.loginPage.contentImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.registerPage.bannerImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.registerPage.bannerImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.registerPage.bannerImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.registerPage.bannerImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.registerPage.contentImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.registerPage.contentImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.registerPage.contentImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.registerPage.contentImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(lpEvent.customContent.homePage.bannerImage)) {
      try {
        const imgBase64: any = await toBase64(lpEvent.customContent.homePage.bannerImage);
        const res: any = await authRepository.uploadFile({
          name: lpEvent.customContent.homePage.bannerImage.name,
          base64: imgBase64,
        });
        lpEvent.customContent.homePage.bannerImage = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    const response = await lpEventsRepository.updateLpEvent(id, lpEvent);

    if (response.isLeft()) {
      set({ isLoadingUpdateLpEvent: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUpdateLpEvent: false });
    }
  },

  resetLpEventDetails: () => set({ lpEventDetails: null }),

  getLpEventsByBusinessUnit: async (businessUnitId: string) => {
    set({ isLoadingLpEventsByBusinessUnit: true });

    const response = await lpEventsRepository.getLpEventsByBusinessUnit(businessUnitId);

    if (response.isLeft()) {
      set({ isLoadingLpEventsByBusinessUnit: false });
      throw new Error(response.value.message);
    } else {
      const lpEventsByBusinessUnit = (response.value || []).map((item: any) => ({
        value: item._id,
        label: item.name,
      }));
      set({ lpEventsByBusinessUnit, isLoadingLpEventsByBusinessUnit: false });
    }
  },
}));
